import { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Badge } from '../../../components/Badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import { Loader2, Search, UserCog, X, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { useReportingManagerStore, ReportingLine } from '../../../store/reportingManagerStore';

/**
 * Admin CRUD over an employee's reporting managers (people.employee_managers,
 * with the primary mirrored to employees.manager_id). An employee may have more
 * than one; any of them can approve that employee's leave / WFH / attendance
 * requests. The first selected manager is the primary (used by the org chart).
 */
export function ReportingManagers() {
  const {
    lines, candidates, isLoading, error,
    fetchLines, fetchCandidates, setManagers, clearManagers,
  } = useReportingManagerStore();

  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<ReportingLine | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [pickSearch, setPickSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLines();
    fetchCandidates();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return lines;
    return lines.filter((l) =>
      `${l.firstName} ${l.lastName}`.toLowerCase().includes(q) ||
      l.employeeCode.toLowerCase().includes(q) ||
      (l.department || '').toLowerCase().includes(q) ||
      l.managers.some((m) => `${m.firstName} ${m.lastName}`.toLowerCase().includes(q)),
    );
  }, [lines, search]);

  const pickable = useMemo(() => {
    const q = pickSearch.trim().toLowerCase();
    return candidates
      .filter((c) => c.id !== editing?.id)
      .filter((c) => !q ||
        `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) ||
        c.employeeCode.toLowerCase().includes(q) ||
        (c.department || '').toLowerCase().includes(q));
  }, [candidates, editing, pickSearch]);

  const openEditor = (line: ReportingLine) => {
    setEditing(line);
    // Keep primary first so it stays primary on save.
    const ordered = [...line.managers].sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));
    setSelected(ordered.map((m) => m.id));
    setPickSearch('');
  };

  const toggle = (id: string) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await setManagers(editing.id, selected);
      toast.success(selected.length ? 'Reporting managers updated' : 'Reporting managers cleared');
      setEditing(null);
    } catch (e: any) {
      toast.error(e.response?.data?.message || e.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async (line: ReportingLine) => {
    try {
      await clearManagers(line.id);
      toast.success(`Cleared reporting managers for ${line.firstName}`);
    } catch (e: any) {
      toast.error(e.response?.data?.message || e.message || 'Failed to clear');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div>
          <h3 className="text-foreground font-display text-section-title flex items-center gap-2">
            <UserCog className="w-4 h-4 text-primary" /> Reporting Managers
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            Assign one or more reporting managers per employee. Any of them can approve that
            person's leave, WFH and attendance requests. The first is the primary (org chart).
          </p>
        </div>
        <div className="relative w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search employees…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-3 p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">No employees found.</div>
        ) : (
          <div className="overflow-x-auto border border-border rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/40 text-left text-xs text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Employee</th>
                  <th className="px-4 py-2 font-medium">Department</th>
                  <th className="px-4 py-2 font-medium">Reporting Managers</th>
                  <th className="px-4 py-2 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((line) => (
                  <tr key={line.id} className="border-b border-border last:border-0 hover:bg-secondary/20 align-top">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-foreground">{line.firstName} {line.lastName}</div>
                      <div className="text-xs text-muted-foreground">#{line.employeeCode}</div>
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{line.department || '—'}</td>
                    <td className="px-4 py-2.5">
                      {line.managers.length ? (
                        <div className="flex flex-wrap gap-1.5">
                          {line.managers.map((m) => (
                            <Badge key={m.id} variant={m.isPrimary ? 'primary' : 'secondary'}>
                              {m.firstName} {m.lastName}{m.isPrimary ? ' ★' : ''}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">Not assigned</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="secondary" size="sm" onClick={() => openEditor(line)}>
                          {line.managers.length ? 'Edit' : 'Assign'}
                        </Button>
                        {line.managers.length > 0 && (
                          <Button variant="ghost" size="sm" onClick={() => handleClear(line)} title="Clear all">
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {/* Assign / edit dialog — multi-select */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reporting managers</DialogTitle>
            <DialogDescription>
              {editing && <>Select everyone <strong>{editing.firstName} {editing.lastName}</strong> reports to. The first selected is the primary.</>}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search people…"
                value={pickSearch}
                onChange={(e) => setPickSearch(e.target.value)}
              />
            </div>

            {selected.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {selected.map((id, i) => {
                  const c = candidates.find((x) => x.id === id);
                  if (!c) return null;
                  return (
                    <Badge key={id} variant={i === 0 ? 'primary' : 'secondary'} className="gap-1">
                      {c.firstName} {c.lastName}{i === 0 ? ' ★' : ''}
                      <button type="button" onClick={() => toggle(id)} className="ml-1 hover:text-destructive">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}

            <div className="max-h-64 overflow-y-auto border border-border rounded-md divide-y divide-border">
              {pickable.length === 0 ? (
                <div className="p-3 text-xs text-muted-foreground text-center">No matching people.</div>
              ) : pickable.map((c) => {
                const on = selected.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggle(c.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm transition-colors ${on ? 'bg-primary/5' : 'hover:bg-secondary/30'}`}
                  >
                    <span>
                      <span className="font-medium text-foreground">{c.firstName} {c.lastName}</span>
                      <span className="text-xs text-muted-foreground"> #{c.employeeCode}{c.department ? ` · ${c.department}` : ''}</span>
                    </span>
                    <span className={`flex items-center justify-center w-5 h-5 rounded border ${on ? 'bg-primary border-primary text-primary-foreground' : 'border-border'}`}>
                      {on && <Check className="w-3.5 h-3.5" />}
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              An employee cannot manage themselves, and assignments that would create a reporting loop are rejected.
            </p>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
